import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Play, Search, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/SmoothScroll";
import { useLocation, useNavigate } from "react-router-dom";
import { ContactDialog } from "@/components/ContactDialog";
import { SearchOverlay } from "@/components/SearchOverlay";
import { useYouTube } from "@/context/YouTubeContext";

export const Wordmark = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="wordmark"
    className="flex items-baseline gap-1 font-display text-sm font-extrabold tracking-[0.18em] text-cream sm:text-base"
    aria-label="Itlu Mee Jaffar — home"
  >
    ITLU MEE JAFFAR
    <span className="mb-0.5 inline-block h-1.5 w-1.5 bg-ember" aria-hidden="true" />
  </button>
);

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const { latestUrl } = useYouTube();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Section links scroll on the home page; from inner pages they navigate home first (Page scrolls to the hash).
  const go = (link) => {
    setMenuOpen(false);
    if (link.action === "contact") return setContactOpen(true);
    if (link.to) return navigate(link.to);
    if (location.pathname !== "/") return navigate(`/#${link.id}`);
    scrollToId(link.id);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
          scrolled ? "border-line bg-ink/80" : "border-transparent bg-ink/40"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 md:px-8">
          <Wordmark onClick={() => go({ id: "home" })} />

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {t.nav.links.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => go(link)}
                data-testid={`nav-${link.id}`}
                className="text-[13px] font-medium text-ash transition-colors duration-200 hover:text-cream"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setLang(lang === "en" ? "te" : "en")}
              data-testid="language-toggle"
              className="min-h-10 rounded-full border border-line px-3.5 py-2 text-xs font-semibold text-ash transition-colors duration-200 hover:border-ember hover:text-cream"
              aria-label="Toggle language"
            >
              {lang === "en" ? "తెలుగు" : "EN"}
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="search-open-button"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ash transition-colors duration-200 hover:border-ember hover:text-cream"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <a
              href={latestUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="watch-latest-button"
              className="hidden items-center gap-2 rounded-full bg-ember px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink transition-colors duration-200 hover:bg-emberHover sm:flex"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              {t.nav.watchLatest}
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-button"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink/95 backdrop-blur-xl"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Wordmark onClick={() => go({ id: "home" })} />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {t.nav.links.map((link, i) => (
                <motion.button
                  key={link.id}
                  type="button"
                  onClick={() => go(link)}
                  data-testid={`mobile-nav-${link.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-line/60 py-4 text-left font-display text-3xl font-bold text-cream transition-colors duration-200 hover:text-ember"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.a
                href={latestUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-watch-latest-button"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex w-fit items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold uppercase tracking-wider text-ink"
              >
                <Play className="h-4 w-4 fill-current" />
                {t.nav.watchLatest}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
};
